import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { AlertCircle } from 'lucide-react';

const AddressAutocomplete = ({ value, onChange, placeholder, className, onLocationSelect, ...props }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isSelectingPlace, setIsSelectingPlace] = useState(false);
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    // Override Google Maps error dialogs to prevent blocking popups
    if (window.google && window.google.maps) {
      // Suppress Google Maps error alerts
      const originalAlert = window.alert;
      const originalConsoleError = console.error;
      
      // Intercept Google Maps errors
      window.gm_authFailure = function() {
        console.warn('Google Maps API authentication failed - using fallback input');
        setApiError(true);
      };
      
      // Prevent Google error modals
      window.alert = function(message) {
        if (message && (
          message.includes('Google') || 
          message.includes('API') ||
          message.includes('maps') ||
          message.includes('quota') ||
          message.includes('RefererNotAllowedMapError')
        )) {
          console.warn('Google Maps alert suppressed:', message);
          setApiError(true);
          return;
        }
        originalAlert(message);
      };
      
      // Suppress Google Maps console errors
      console.error = function(...args) {
        const errorStr = args.join(' ');
        if (errorStr.includes('Google Maps') || 
            errorStr.includes('googleapis.com') ||
            errorStr.includes('RefererNotAllowedMapError')) {
          console.warn('Google Maps error suppressed:', ...args);
          setApiError(true);
          return;
        }
        originalConsoleError.apply(console, args);
      };
      
      return () => {
        window.alert = originalAlert;
        console.error = originalConsoleError;
      };
    }
  }, []);

  useEffect(() => {
    // Check if Google Maps API is loaded and accessible
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps API not loaded, using regular input');
      return;
    }

    // Check for API errors
    if (apiError) {
      console.log('Google Maps API error detected, using regular input');
      return;
    }

    // Initialize Google Places Autocomplete
    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: 'za' }, // Restrict to South Africa
          fields: ['formatted_address', 'address_components', 'geometry'],
          types: ['address']
        }
      );

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (place && place.formatted_address) {
          setIsSelectingPlace(true);
          
          // Set the selected address using the onChange handler
          onChange({
            target: {
              name: props.name,
              value: place.formatted_address
            }
          });

          // Also set the input value directly to ensure it stays visible
          if (inputRef.current) {
            inputRef.current.value = place.formatted_address;
          }

          // If onLocationSelect callback is provided, pass coordinates
          if (onLocationSelect && place.geometry && place.geometry.location) {
            const location = {
              address: place.formatted_address,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng()
            };
            onLocationSelect(location);
          }

          // Reset the flag after a short delay
          setTimeout(() => {
            setIsSelectingPlace(false);
          }, 200);
        }
      });
    } catch (error) {
      console.log('Google Places autocomplete initialization failed:', error);
      setApiError(true);
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch (e) {
          console.log('Error clearing listeners:', e);
        }
      }
    };
  }, [onChange, props.name, onLocationSelect, apiError]);

  // Sync the input value when the value prop changes
  useEffect(() => {
    if (inputRef.current && value && !isSelectingPlace) {
      inputRef.current.value = value;
    }
  }, [value, isSelectingPlace]);

  // Handle manual input changes
  const handleInputChange = (e) => {
    // Don't override if we're currently selecting a place from autocomplete
    if (!isSelectingPlace) {
      onChange(e);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder || "Enter your address"}
        className={className}
        autoComplete="off"
        {...props}
      />
      {apiError && (
        <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
          <AlertCircle className="w-3 h-3" />
          <span>Autocomplete unavailable - please type your full address</span>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
